'use client';

import Link from 'next/link';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { getBranchUrl } from 'app/utility/navigation';
import { cleanPhone } from 'app/utility/helpers';
import { IFile, IMenuItem, IMessenger, ISettings } from 'app/interfaces/common';
import { IBranchEssential } from 'app/interfaces/branches';
import FeedbackForm from './FeedbackForm';
import { defaultProcessText } from 'app/utility/textProcessing';
import ImageBg from 'app/components/common/ImageBg';

export default function MenuModal({
  items,
  branches,
  settings,
  socials,
  messengers,
  isOpen,
  close,
  onOverlayClick,
  privacy_policy_file
}: {
  items: IMenuItem[];
  branches: IBranchEssential[];
  settings: ISettings | null;
  socials: IMenuItem[];
  messengers: IMessenger[];
  isOpen: boolean;
  close: () => void;
  onOverlayClick?: () => void;
  privacy_policy_file?: IFile | null;
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ ease: 'easeInOut', duration: 0.6 }}
          className="fixed inset-0 xl:pt-5 xl:wrapper-main"
        >
          <div
            className="absolute inset-0 bg-black opacity-30 rounded"
            onClick={() => {
              close();
              onOverlayClick?.call(null);
            }}
          ></div>
          <div className="bg-sand-dark xl:rounded-xl px-3 md:px-6 xl:px-3 pt-5 xl:pt-3 pb-20 xl:pb-10 relative max-h-full overflow-auto">
            <div className="flex flex-col gap-10 xl:gap-20">
              <div className="flex flex-col xl:flex-row gap-5 xl:gap-2">
                {!!items?.length && (
                  <ul className="grid grid-cols-2 gap-2 md:gap-3 xl:gap-2 lg:grid-cols-4 xl:grid-cols-8 flex-grow">
                    {items.map((item, index) => (
                      <li
                        key={index}
                        className={classNames({
                          'col-start-1 md:col-start-2': index === 1
                        })}
                      >
                        <Link href={item.url} className="group">
                          <div className="relative">
                            <div className="relative pb-[40%] xl:pb-0 xl:h-32 overflow-hidden rounded isolate">
                              {!!item.image?.url && (
                                <div className="absolute inset-0 rounded group-hover:scale-110 transition-transform duration-600">
                                  <ImageBg
                                    src={item.image?.url}
                                    alt={item.name}
                                    fill
                                    placeholderDataURL={
                                      item.image?.placeholder_data_url
                                    }
                                    className="object-cover rounded"
                                  />
                                  <div className="absolute inset-0 bg-black opacity-30 rounded"></div>
                                </div>
                              )}
                            </div>
                            <div className="absolute inset-0">
                              <div className="py-3 px-2 text-p4 uppercase text-white">
                                {item.name}
                              </div>
                            </div>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="absolute md:relative top-5 md:top-auto right-3 md:right-auto flex justify-end -order-1 xl:order-none">
                  <button
                    type="button"
                    className={classNames(
                      'flex justify-center items-center w-12 min-h-12 xl:w-10 xl:min-h-10 group',
                      {
                        open: isOpen
                      }
                    )}
                    onClick={close}
                  >
                    <span className="flex flex-col gap-1 relative">
                      <span className="w-[3px] h-[3px] rounded-full bg-graphite absolute top-0 left-0 group-[&.open]:translate-x-2 transition-transform duration-300"></span>
                      <span className="w-[3px] h-[3px] rounded-full bg-graphite left-0 group-[&.open]:-translate-x-2 transition-transform duration-300"></span>
                      <span className="w-[3px] h-[3px] rounded-full bg-graphite"></span>
                      <span className="w-[3px] h-[3px] rounded-full bg-graphite group-[&.open]:-translate-x-2 transition-transform duration-300"></span>
                      <span className="w-[3px] h-[3px] rounded-full bg-graphite absolute bottom-0 left-0 group-[&.open]:translate-x-2 transition-transform duration-300"></span>
                    </span>
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-10 text-base md:text-xl md:grid md:grid-cols-12 md:gap-x-3 xl:gap-4 xl:pr-13">
                {!!branches?.length && (
                  <ul className="flex flex-col gap-4 md:col-span-6 xl:col-span-4 xl:col-start-1">
                    {branches.map((item, index) => {
                      return (
                        <li key={index}>
                          <Link href={getBranchUrl(item)}>{item.address}</Link>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {!!socials?.length && (
                  <ul className="flex flex-wrap gap-4 md:justify-end xl:justify-start md:col-span-6 xl:col-span-4 xl:col-start-1 xl:row-start-2 xl:self-end">
                    {socials.map((item, index) => {
                      return (
                        <li key={index}>
                          <a
                            className="hover:opacity-70 transition-opacity duration-300 lowercase"
                            href={item.url}
                            target="_blank"
                            rel="nofollow"
                          >
                            {item.name}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                )}

                <div className="md:col-span-12 xl:col-span-3 xl:col-start-6 xl:row-start-1">
                  {!!settings?.feedback_form_description && (
                    <div
                      className="max-w-[280px]"
                      dangerouslySetInnerHTML={{
                        __html: defaultProcessText(
                          settings.feedback_form_description
                        )
                      }}
                    ></div>
                  )}
                </div>
                <div className="md:col-span-12 xl:col-span-4 xl:row-span-2">
                  <FeedbackForm privacy_policy_file={privacy_policy_file} />
                </div>
                {(!!settings?.phone || !!messengers?.length) && (
                  <div className="flex justify-between gap-2 xl:flex-col md:col-span-12 xl:col-span-3 xl:col-start-6 xl:self-end">
                    {!!messengers?.length && (
                      <div className="flex flex-col gap-2 lowercase">
                        {messengers.map((item, index) => {
                          return (
                            <a
                              key={index}
                              href={item.url}
                              target="_blank"
                              rel="nofollow"
                            >
                              {item.name}
                            </a>
                          );
                        })}
                      </div>
                    )}
                    {!!settings?.phone && (
                      <a
                        href={`tel:${cleanPhone(settings.phone)}`}
                        target="_blank"
                        rel="nofollow"
                      >
                        {settings.phone}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
